import { FILE_ALLOWED_CONTENT_TYPES, FILE_MAX_SIZE_BYTES, resolveContentType } from '@atlas/types'
import { fileTypeToken, formatBytes } from '@atlas/ui-domain'
import type { CampaignUploadFailureReason } from '@/domain/campaign-ports'
import { isEmptyUploadSize } from '@/upload/upload-precheck'

/**
 * The verdict for one staged File. The rejection reasons are deliberately the
 * same literals the upload capability reports, so an immediate rejection and an
 * execution-time rejection read from one copy vocabulary.
 */
export type CampaignUploadSelection =
	| { readonly status: 'valid'; readonly file: File }
	| { readonly status: 'rejected'; readonly file: File; readonly reason: CampaignUploadSelectionRejection }

export type CampaignUploadSelectionRejection = Extract<
	CampaignUploadFailureReason,
	'invalid-file-type' | 'file-too-large' | 'empty-file'
>

/**
 * Extensions listed in `accept` alongside the MIME types.
 *
 * A picker matches an `accept` MIME entry against the BROWSER's own type for the
 * file, and browsers do not report the registered type for these formats: an `.ai`
 * comes through as `application/pdf`, `application/illustrator` or
 * `application/octet-stream`, and a `.psd` as an empty string on Safari. So a
 * MIME-only list greys out files the validator (which normalises first) accepts —
 * the picker refuses to let the user choose a file the app would take.
 *
 * Extensions are matched literally by filename, which is what closes that gap.
 * Only formats whose browser type is unreliable need an entry.
 *
 * The fonts are here for the same reason and more strongly: `font/*` was only
 * registered in 2017, so a browser may report the registered type, a legacy
 * `application/x-font-*` spelling, or nothing at all for the same file.
 */
const UNRELIABLY_TYPED_EXTENSIONS = [
	'.ai',
	'.psd',
	'.tif',
	'.tiff',
	'.heic',
	'.avif',
	'.woff',
	'.woff2',
	'.otf',
	'.ttf',
] as const

/**
 * The native input's `accept` value, derived from the one shared allowed-type
 * list rather than restated. It is SELECTION GUIDANCE ONLY, never enforcement:
 * an OS picker can be switched to "All Files", and a drag-and-drop never
 * consults `accept` at all. `validateCampaignUploadSelection` is the
 * presentation gate, and the upload adapter's own pre-validation remains the
 * authoritative execution boundary.
 */
export const CAMPAIGN_UPLOAD_ACCEPT = [...FILE_ALLOWED_CONTENT_TYPES, ...UNRELIABLY_TYPED_EXTENSIONS].join(',')

/**
 * Pre-selection guidance, derived from the very same shared constraints — the
 * permitted types collapsed to their recognisable markers through the existing
 * `fileTypeToken`, and the ceiling formatted through the existing `formatBytes`.
 * Nothing here is hand-maintained, so the guidance cannot drift from what the
 * validator and the execution boundary actually enforce.
 */
export const CAMPAIGN_UPLOAD_GUIDANCE = `Accepted formats: ${[
	...new Set(FILE_ALLOWED_CONTENT_TYPES.map((contentType) => fileTypeToken(contentType))),
].join(', ')}. Maximum size: ${formatBytes(FILE_MAX_SIZE_BYTES)}.`

/**
 * Pure, synchronous presentation gate for one staged File. Reads the same
 * shared constraints the upload adapter's pre-validation reads, so the two
 * cannot diverge — an unrecognised content type fails the allow-list check and
 * is rejected as `invalid-file-type`, exactly as execution would decide. Checks
 * emptiness before type and type before size, matching that pre-validation's
 * order. Size exactly at the maximum is permitted; one byte above it is not. No
 * filename rule is applied, because the repository defines none.
 */
export function validateCampaignUploadSelection(file: File): CampaignUploadSelection {
	// First, so an empty file is named as empty rather than as whatever its type
	// happens to look like — and so the user hears it at selection instead of
	// after pressing Upload, which is when the adapter would otherwise say it.
	if (isEmptyUploadSize(file.size)) {
		return { status: 'rejected', file, reason: 'empty-file' }
	}
	// Resolved, not `file.type` raw — the same normalisation the upload adapter
	// applies. Testing the raw value rejected files the adapter would have
	// accepted: browsers report non-canonical types for formats they do not know
	// (an .ai as `application/illustrator`, a .psd as an empty string on Safari),
	// so the gate turned away a valid file with no request and no trace.
	const contentType = resolveContentType(file.name, file.type)
	if (!(FILE_ALLOWED_CONTENT_TYPES as readonly string[]).includes(contentType)) {
		return { status: 'rejected', file, reason: 'invalid-file-type' }
	}
	if (file.size > FILE_MAX_SIZE_BYTES) {
		return { status: 'rejected', file, reason: 'file-too-large' }
	}
	return { status: 'valid', file }
}
